'use client'

import type { PortableTextReactComponents } from '@portabletext/react'
import { PortableText } from '@portabletext/react'
import { motion, useReducedMotion } from 'framer-motion'
import { useState } from 'react'

import type { FaqPage } from '@/lib/apis/sanity'
import {
  softEditorialEase,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'
import { ScrollReveal } from '@/shared/components/motion/ScrollReveal'

type RedesignFaqDirectoryProps = {
  sections: FaqPage['sections']
}

const accents = ['#7C5CD6', '#EF8E3B', '#43BBA9', '#2563EB', '#5A6478']

const portableTextComponents: Partial<PortableTextReactComponents> = {
  block: {
    normal: ({ children }) => <p>{children}</p>,
  },
  list: {
    bullet: ({ children }) => (
      <ul className="mt-2 flex flex-col gap-2">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="mt-2 list-decimal space-y-2 pl-5">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="flex items-start gap-2.5">
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#43BBA9]" />
        <span>{children}</span>
      </li>
    ),
    number: ({ children }) => <li>{children}</li>,
  },
  marks: {
    link: ({ value, children }) => (
      <a
        href={value?.href}
        target="_blank"
        rel="noreferrer"
        className="font-semibold text-[#2563EB] underline-offset-4 hover:underline"
      >
        {children}
      </a>
    ),
  },
  hardBreak: false,
}

export function RedesignFaqDirectory({ sections }: RedesignFaqDirectoryProps) {
  const [openQuestions, setOpenQuestions] = useState<Set<string>>(
    () => new Set(sections[0]?.list.questions.length ? ['0-0'] : []),
  )
  const prefersReducedMotion = useReducedMotion()

  const toggleQuestion = (key: string) => {
    setOpenQuestions((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <section className="mx-auto max-w-[1440px] px-5 pb-12 lg:px-[140px] lg:pb-24">
      <nav
        aria-label="FAQ categories"
        className="flex flex-col gap-3 border-y border-[#ECECEF] py-4 lg:flex-row lg:items-center lg:gap-10 lg:py-[18px]"
      >
        <span className="shrink-0 text-[11px] font-semibold uppercase leading-[14px] tracking-[0.06em] text-[#82828B] lg:text-[11.5px]">
          Jump to
        </span>
        <div className="flex flex-wrap items-baseline gap-x-[22px] gap-y-[11px] lg:gap-8">
          {sections.map((section, index) => (
            <a
              key={`${section.title}-${index}`}
              href={`#faq-section-${index}`}
              className="flex items-baseline gap-1.5 text-sm font-semibold leading-[19px] underline decoration-[#C4C4CA] decoration-1 underline-offset-4 transition-colors duration-500 hover:text-[#2563EB] lg:gap-[7px] lg:text-[14.5px]"
            >
              {section.title}
              <span className="text-xs font-normal leading-[15px] text-[#A0A0A8] lg:text-[12.5px] lg:leading-4">
                {section.list.questions.length}
              </span>
            </a>
          ))}
        </div>
      </nav>

      <div className="mt-10 flex flex-col gap-[52px] lg:mt-12 lg:gap-[72px]">
        {sections.map((section, sectionIndex) => (
          <ScrollReveal
            key={`${section.title}-${sectionIndex}`}
            distance={10}
            viewportAmount={0.04}
          >
            <div id={`faq-section-${sectionIndex}`} className="scroll-mt-28">
              <div className="flex items-center gap-[9px] pb-1.5 lg:gap-2.5 lg:pb-2.5">
                <span
                  className="h-[7px] w-[7px] shrink-0 rounded-full"
                  style={{
                    backgroundColor: accents[sectionIndex % accents.length],
                  }}
                />
                <h2 className="font-jakarta text-[21px] font-bold leading-[27px] tracking-[-0.02em] lg:text-[26px] lg:leading-[33px]">
                  {section.title}
                </h2>
              </div>

              <div>
                {section.list.questions.map((question, questionIndex) => {
                  const key = `${sectionIndex}-${questionIndex}`
                  const open = openQuestions.has(key)
                  const panelId = `faq-answer-${sectionIndex}-${questionIndex}`

                  return (
                    <div
                      key={key}
                      className="border-b border-[#ECECEF] py-4 lg:py-5"
                    >
                      <button
                        type="button"
                        aria-expanded={open}
                        aria-controls={panelId}
                        onClick={() => toggleQuestion(key)}
                        className="flex w-full items-center justify-between gap-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2563EB] lg:gap-6"
                      >
                        <span className="max-w-[700px] text-[15.5px] font-semibold leading-[21px] tracking-[-0.01em] lg:text-[17px] lg:leading-[23px]">
                          {question.question}
                        </span>
                        <span className="relative h-[18px] w-[18px] shrink-0 text-[#2563EB] lg:h-5 lg:w-5">
                          <span className="absolute left-0 top-1/2 h-[2px] w-full -translate-y-1/2 rounded-full bg-current" />
                          <motion.span
                            className="absolute left-1/2 top-0 h-full w-[2px] -translate-x-1/2 rounded-full bg-current"
                            animate={{
                              scaleY: open ? 0 : 1,
                              opacity: open ? 0 : 1,
                            }}
                            transition={
                              prefersReducedMotion
                                ? { duration: 0 }
                                : {
                                    duration: softMotionDuration.panel,
                                    ease: softEditorialEase,
                                  }
                            }
                          />
                        </span>
                      </button>

                      <motion.div
                        id={panelId}
                        initial={false}
                        animate={{
                          height: open ? 'auto' : 0,
                          opacity: open ? 1 : 0,
                        }}
                        transition={
                          prefersReducedMotion
                            ? { duration: 0 }
                            : {
                                duration: softMotionDuration.reveal,
                                ease: softEditorialEase,
                              }
                        }
                        className="overflow-hidden"
                      >
                        <div className="max-w-[720px] pt-2.5 text-sm leading-[21px] text-[#82828B] lg:pt-3 lg:text-[15.5px] lg:leading-6">
                          <PortableText
                            value={question.answer}
                            components={portableTextComponents}
                          />
                        </div>
                      </motion.div>
                    </div>
                  )
                })}
              </div>
            </div>
          </ScrollReveal>
        ))}
      </div>
    </section>
  )
}
